// src/interest.js
// Pipeline interest levels (Reece, 10/9/26): "I wanted to be able to
// categorize interest levels, just to be able to look at the pipeline in full
// and see what's going on."
//
// Two things per FILE, both his own call, set by hand:
//   interest  null | "good" | "fence" | "bad"   — how he feels about the deal
//   booked    boolean                          — a switch he flips himself;
//             NOT derived from the stage (moving stages never changes it)
// (+ interestAt / bookedAt — ISO stamps of when each was last set.)
//
// What it looks like (interestStyle): the name's TEXT colour is always the
// feeling — Feel good = green, On the fence = blue, Don't feel good = red,
// nothing set = normal text — and a YELLOW HIGHLIGHT always means booked. So
// booked + feel good = yellow background, green text; booked with no feeling
// = yellow background, normal text. Colours are theme tokens (C.feelGood …
// C.booked), chosen to stay ≥5:1 on white and on the yellow.
//
// Persistence: the fields ride in follow_ups._wf (lib.js wfPack / loanFromDb).
// The App merge (mergeLoanRow) keeps a change made on another device; when
// both sides changed the same one, the newer stamp wins. Never sent to the
// borrower portal (api/borrower/loan.js strips _wf). Mail-list leads have no
// interest level — only files do.
//
// Pure: no React here (src/views/Interest.jsx is the control + filter bar).
import { C } from './theme';

const INTEREST_LEVELS=["good","fence","bad"];
const INTEREST_META={
  good:{key:"good",label:"Feel good",color:C.feelGood},
  fence:{key:"fence",label:"On the fence",color:C.feelFence},
  bad:{key:"bad",label:"Don't feel good",color:C.feelBad},
};
const INTEREST_NORMAL_LABEL="Normal";
const BOOKED_LABEL="Booked";
const INTEREST_NONE_LABEL="Not set";

function interestOf(loan){var v=loan&&loan.interest;return INTEREST_LEVELS.indexOf(v)>=0?v:null;}
function isBooked(loan){return !!(loan&&loan.booked===true);}

// {color, background, label} for a file — color null = normal text,
// background null = no highlight, label "" = nothing set. Also `level`
// (null | good | fence | bad) and `booked` for callers that need them.
function interestStyle(loan){
  var lv=interestOf(loan);var bk=isBooked(loan);
  var parts=[];
  if(lv)parts.push(INTEREST_META[lv].label);
  if(bk)parts.push(BOOKED_LABEL);
  return{color:lv?INTEREST_META[lv].color:null,background:bk?C.booked:null,label:parts.join(" · "),level:lv,booked:bk};
}
// Inline style for the borrower's name: the feeling's colour (or nothing).
function interestNameStyle(loan){var s=interestStyle(loan);return s.color?{color:s.color}:{};}

// A copy of the loan with the patch applied ({interest} and/or {booked}),
// stamping interestAt / bookedAt for whichever actually changed — also when a
// feeling is cleared back to normal, so the clear is a dated change the merge
// can order. Nothing changed → the SAME object (no save is triggered).
function withInterest(loan,patch,now){
  if(!loan||!patch)return loan;
  var ts=(now instanceof Date?now:new Date()).toISOString();
  var n=null;
  if(Object.prototype.hasOwnProperty.call(patch,"interest")){
    var lv=INTEREST_LEVELS.indexOf(patch.interest)>=0?patch.interest:null;
    if(lv!==interestOf(loan)){n=Object.assign({},loan,{interest:lv,interestAt:ts});}
  }
  if(Object.prototype.hasOwnProperty.call(patch,"booked")){
    var bk=patch.booked===true;
    if(bk!==isBooked(loan)){n=Object.assign(n||Object.assign({},loan),{booked:bk,bookedAt:ts});}
  }
  return n||loan;
}
// setLoans updater: the one file `id` gets the patch; when nothing changes
// the SAME array comes back, so the autosave sees no edit.
function applyInterest(prev,id,patch,now){
  var arr=prev||[];var hit=false;
  var next=arr.map(function(l){if(!l||l.id!==id)return l;var n=withInterest(l,patch,now);if(n!==l)hit=true;return n;});
  return hit?next:arr;
}

// ── "See what's going on": counts + est. comp per level, and Booked ──
// {good, fence, bad, none, booked: {n, comm}, total}
function interestSummary(loans){
  var out={good:{n:0,comm:0},fence:{n:0,comm:0},bad:{n:0,comm:0},none:{n:0,comm:0},booked:{n:0,comm:0},total:0};
  (loans||[]).forEach(function(l){
    if(!l)return;
    var c=Number(l.comm)||0;
    var k=interestOf(l)||"none";
    out[k].n++;out[k].comm+=c;
    if(isBooked(l)){out.booked.n++;out.booked.comm+=c;}
    out.total++;
  });
  return out;
}

// ── Sorting a lane by interest (Reece, 10/10/26): "automatically filter the
// statuses based on the least interested, to most interested, based on the
// colors". Don't feel good (red) → On the fence (blue) → Not set → Feel good
// (green); at the same level not booked before booked (yellow). STABLE — ties
// keep the order they came in (the board's previous default order). A
// mail-list lead (no file → null) reads as Not set, not booked.
var INTEREST_SORT_ORDER=["bad","fence","none","good"];
function interestSortRank(loan){return INTEREST_SORT_ORDER.indexOf(interestOf(loan)||"none")*2+(isBooked(loan)?1:0);}
// A sorted COPY; loanOf(item) → the file to rank it by (default: the item).
function sortByInterest(arr,loanOf){
  var get=loanOf||function(x){return x;};
  return (arr||[]).map(function(x,i){return{x:x,i:i,r:interestSortRank(get(x))};})
    .sort(function(a,b){return a.r-b.r||a.i-b.i;})
    .map(function(d){return d.x;});
}

// ── The board filter ──
// {levels: subset of ["good","fence","bad","none"], booked: boolean}.
// Levels are OR'd (Feel good + On the fence = either); Booked narrows to
// booked files (with no level picked: every booked file). Empty = everything.
var FILTER_LEVELS=INTEREST_LEVELS.concat(["none"]);
var INTEREST_FILTER_EMPTY=Object.freeze({levels:Object.freeze([]),booked:false});
function normalizeInterestFilter(f){
  var lv=[];
  ((f&&Array.isArray(f.levels))?f.levels:[]).forEach(function(k){if(FILTER_LEVELS.indexOf(k)>=0&&lv.indexOf(k)<0)lv.push(k);});
  lv.sort(function(a,b){return FILTER_LEVELS.indexOf(a)-FILTER_LEVELS.indexOf(b);});
  return{levels:lv,booked:!!(f&&f.booked===true)};
}
function interestFilterActive(f){return !!f&&((f.levels&&f.levels.length>0)||f.booked===true);}
function interestFilterMatch(loan,f){
  if(!interestFilterActive(f))return true;
  if(f.booked&&!isBooked(loan))return false;
  if(f.levels&&f.levels.length&&f.levels.indexOf(interestOf(loan)||"none")<0)return false;
  return true;
}
function toggleInterestFilter(f,key){
  var cur=normalizeInterestFilter(f);
  if(key==="booked")return{levels:cur.levels,booked:!cur.booked};
  if(FILTER_LEVELS.indexOf(key)<0)return cur;
  var lv=cur.levels.indexOf(key)>=0?cur.levels.filter(function(k){return k!==key;}):cur.levels.concat([key]);
  return normalizeInterestFilter({levels:lv,booked:cur.booked});
}
// Remembered per browser (this device only), like the Mine · Max · All
// choice. Every read / write is guarded — private windows can throw.
var INTEREST_FILTER_KEY="loanflow_interest_filter";
function readInterestFilter(){
  try{var raw=localStorage.getItem(INTEREST_FILTER_KEY);return normalizeInterestFilter(raw?JSON.parse(raw):null);}
  catch(e){return normalizeInterestFilter(null);}
}
function writeInterestFilter(f){
  try{
    if(interestFilterActive(f))localStorage.setItem(INTEREST_FILTER_KEY,JSON.stringify(normalizeInterestFilter(f)));
    else localStorage.removeItem(INTEREST_FILTER_KEY);
  }catch(e){/* storage blocked — the filter just isn't remembered */}
}

export {
  INTEREST_LEVELS, INTEREST_META, INTEREST_NORMAL_LABEL, INTEREST_NONE_LABEL, BOOKED_LABEL,
  interestOf, isBooked, interestStyle, interestNameStyle, withInterest, applyInterest,
  interestSummary, INTEREST_SORT_ORDER, interestSortRank, sortByInterest,
  FILTER_LEVELS, INTEREST_FILTER_EMPTY, INTEREST_FILTER_KEY, normalizeInterestFilter,
  interestFilterActive, interestFilterMatch, toggleInterestFilter, readInterestFilter, writeInterestFilter,
};
